// frontend/src/components/AddMedicationForm/AddMedicationForm.tsx
import React, { useEffect, useMemo, useState } from 'react';
import './AddMedicationForm.css';
import { useAuth0 } from '@auth0/auth0-react';
import { fetchWithAuth } from '../../api/fetchWithAuth';

type ScheduleType = 'daily' | 'weekly';

interface MedicationOption {
  name: string;
  dose: string;
  scheduleType?: ScheduleType;
}

type NewMed = {
  name: string;
  times: string[];
  indication: string;
  instruction: string;
  removed?: boolean;
  scheduleType?: ScheduleType;
  doses?: Record<string, number>;
  weekdays?: number[];
  advanced?: Record<number, Record<string, number>>;
  endDate?: { year: number; month: number; day: number };
};

interface AddMedicationFormProps {
  onConfirm: (medication: NewMed) => void;
  onCancel: () => void;
  mode?: 'add' | 'edit';
  initialValues?: {
    name: string;
    times: string[];
    indication: string;
    instruction?: string;
    scheduleType?: ScheduleType;
    doses?: Record<string, number>;
    weekdays?: number[];
    advanced?: Record<number, Record<string, number>>;
    endDate?: { year: number; month: number; day: number };
  };
  scheduleType?: ScheduleType;
}

const dayLabelsShort = ['Mán', 'Þri', 'Mið', 'Fim', 'Fös', 'Lau', 'Sun'];
const dayLabelsLong = [
  'Mánudagur',
  'Þriðjudagur',
  'Miðvikudagur',
  'Fimmtudagur',
  'Föstudagur',
  'Laugardagur',
  'Sunnudagur',
];

const monthsIS = [
  'Janúar',
  'Febrúar',
  'Mars',
  'Apríl',
  'Maí',
  'Júní',
  'Júlí',
  'Ágúst',
  'September',
  'Október',
  'Nóvember',
  'Desember',
];

const AddMedicationForm: React.FC<AddMedicationFormProps> = ({
  onConfirm,
  onCancel,
  mode = 'add',
  initialValues,
  scheduleType: propScheduleType,
}) => {
  // PRE-FILL SEARCH WITH MED NAME WHEN EDITING
  const [query, setQuery] = useState(initialValues?.name ?? '');
  const [medicationOptions, setMedicationOptions] = useState<MedicationOption[]>([]);
  const [filteredOptions, setFilteredOptions] = useState<MedicationOption[]>([]);
  const [selectedMedication, setSelectedMedication] = useState<MedicationOption | null>(null);

  // Basic mode
  const [selectedTimes, setSelectedTimes] = useState<string[]>(initialValues?.times ?? []);
  const [doses, setDoses] = useState<Record<string, number>>(() => {
    // Prefer explicit doses coming from parent
    if (initialValues?.doses) return { ...initialValues.doses };

    const base: Record<string, number> = {};
    (initialValues?.times ?? []).forEach((h) => {
      base[h] = 1;
    });
    return base;
  });

  // Advanced mode
  const [showAdvanced, setShowAdvanced] = useState(false);
  // Advanced mode (dayIdx -> hour -> dose)
  const [advanced, setAdvanced] = useState<Record<number, Record<string, number>>>(
    initialValues?.advanced ? { ...initialValues.advanced } : {}
  );

  // End-date panel
  const [showEndDate, setShowEndDate] = useState(false);
  const thisYear = new Date().getFullYear();
  const years = useMemo(() => Array.from({ length: 6 }, (_, i) => thisYear + i), [thisYear]);
  const [endYear, setEndYear] = useState<number | ''>(initialValues?.endDate?.year ?? '');
  const [endMonth, setEndMonth] = useState<number | ''>(initialValues?.endDate?.month ?? '');
  const [endDay, setEndDay] = useState<number | ''>(initialValues?.endDate?.day ?? '');

  const [indication, setIndication] = useState(initialValues?.indication ?? '');

  // Remove medication workflow
  const [removeMode, setRemoveMode] = useState(false);
  const [removeReason, setRemoveReason] = useState('');

  // Weekly meta
  const [selectedWeekdays, setSelectedWeekdays] = useState<number[]>(
    initialValues?.weekdays ? [...initialValues.weekdays] : []
  );
  // schedule type: prefer initialValues → prop → default
  const [scheduleType, setScheduleType] = useState<ScheduleType>(
    initialValues?.scheduleType ?? propScheduleType ?? 'daily'
  );

  const API_BASE = 'https://api-staging.mitoflux.is';
  const { getAccessTokenSilently } = useAuth0();

  const toggleWeekday = (idx: number) => {
    setSelectedWeekdays((prev) =>
      prev.includes(idx) ? prev.filter((d) => d !== idx) : [...prev, idx].sort((a, b) => a - b)
    );
  };

  useEffect(() => {
    const fetchMedications = async () => {
      try {
        // const res = await fetch('http://localhost:4000/api/medications');
        const res = await fetchWithAuth(getAccessTokenSilently, `${API_BASE}/api/medications`);
        const data = await res.json();
        setMedicationOptions(data);
      } catch (error) {
        console.error('Failed to fetch medications:', error);
      }
    };
    fetchMedications();
  }, [getAccessTokenSilently]);

  // When editing: find the matching option and select it.
  // If scheduleType wasn't set already, use option.scheduleType / prop / 'daily'.
  useEffect(() => {
    if (!initialValues) return;
    if (!medicationOptions.length) return;

    const match = medicationOptions.find(
      (opt) => `${opt.name} ${opt.dose}`.toLowerCase() === initialValues.name.toLowerCase()
    );
    if (!match) return;

    setSelectedMedication(match);

    setScheduleType((prev) => {
      if (prev) return prev;
      return (match.scheduleType as ScheduleType | undefined) ?? propScheduleType ?? 'daily';
    });
  }, [initialValues, medicationOptions, propScheduleType]);

  useEffect(() => {
    if (!query) {
      setFilteredOptions([]);
      return;
    }
    const lowerQuery = query.toLowerCase();
    setFilteredOptions(
      medicationOptions.filter((med) => med.name.toLowerCase().includes(lowerQuery))
    );
  }, [query, medicationOptions]);

  const toggleTime = (hour: string) => {
    // Whenever user interacts with basic times, we drop any advanced schedule.
    setAdvanced({});
    //setShowAdvanced(false); // optional: close advanced panel ATH

    setSelectedTimes((prev) => {
      if (prev.includes(hour)) {
        // remove hour and its dose
        setDoses((d) => {
          const copy = { ...d };
          delete copy[hour];
          return copy;
        });
        return prev.filter((t) => t !== hour);
      } else {
        // add hour with default dose 1
        setDoses((d) => ({ ...d, [hour]: 1 }));
        return [...prev, hour].sort((a, b) => parseInt(a, 10) - parseInt(b, 10));
      }
    });
  };

  const handleDoseChange = (hour: string, value: string) => {
    // const n = Math.max(1, parseInt(value || '1', 10));
    // setDoses((d) => ({ ...d, [hour]: n }));
    const parsed = parseFloat((value || '1').replace(',', '.'));
    const n = Number.isFinite(parsed) ? parsed : 1;
    const clamped = Math.max(0.5, n);
    setDoses((d) => ({ ...d, [hour]: clamped }));
  };

  const toggleAdvancedCell = (dayIdx: number, hour: string) => {
    setAdvanced((prev) => {
      const dayRow = { ...(prev[dayIdx] || {}) };
      const isCurrentlyActive = !!dayRow[hour];

      if (isCurrentlyActive) {
        // turning OFF this advanced cell
        delete dayRow[hour];
      } else {
        // turning ON this advanced cell
        dayRow[hour] = 1; // default dose

        // As soon as we enable *any* advanced cell, clear all basic-time selections
        setSelectedTimes([]);
        setDoses({});
        setSelectedWeekdays([]);
      }

      const next: Record<number, Record<string, number>> = { ...prev, [dayIdx]: dayRow };

      // clean empty day rows
      if (Object.keys(dayRow).length === 0) {
        delete next[dayIdx];
      }

      return next;
    });
  };

  //þetta leyfir ekki 0.5 töflur, bara heilar tölur
  // const setAdvancedDose = (dayIdx: number, hour: string, value: string) => {
  //   const trimmed = value.trim();

  //   setAdvanced((prev) => {
  //     const dayRow = { ...(prev[dayIdx] || {}) };

  //     // If empty or 0 → treat as "unselect this time"
  //     if (trimmed === '' || trimmed === '0') {
  //       delete dayRow[hour];
  //     } else {
  //       const n = parseInt(trimmed, 10);
  //       if (isNaN(n) || n <= 0) {
  //         delete dayRow[hour];
  //       } else {
  //         dayRow[hour] = n;
  //       }
  //     }

  //     const next: Record<number, Record<string, number>> = { ...prev, [dayIdx]: dayRow };

  //     // Clean up empty day rows
  //     if (Object.keys(dayRow).length === 0) {
  //       delete next[dayIdx];
  //     }

  //     return next;
  //   });
  // };

  const setAdvancedDose = (dayIdx: number, hour: string, value: string) => {
    const trimmed = value.trim();

    setAdvanced((prev) => {
      const dayRow = { ...(prev[dayIdx] || {}) };

      // If empty or 0 → treat as "unselect this time"
      if (trimmed === '') {
        delete dayRow[hour];
      } else {
        const parsed = parseFloat(trimmed.replace(',', '.'));

        // invalid or <= 0 → unselect
        if (!Number.isFinite(parsed) || parsed <= 0) {
          delete dayRow[hour];
        } else {
          // allow halves, clamp to minimum 0.5
          dayRow[hour] = Math.max(0.5, parsed);
        }
      }

      const next: Record<number, Record<string, number>> = { ...prev, [dayIdx]: dayRow };

      if (Object.keys(dayRow).length === 0) {
        delete next[dayIdx];
      }

      return next;
    });
  };

  const hasAdvanced = () =>
    Object.keys(advanced).some((d) => Object.keys(advanced[parseInt(d, 10)] || {}).length > 0);

  const word = (n: number) => (n === 1 ? 'tafla' : 'töflur');

  const hasBasicSelection = useMemo(() => {
    if (scheduleType === 'weekly') {
      // Weekly: must have at least one time AND at least one weekday
      return selectedTimes.length > 0 && selectedWeekdays.length > 0;
    }
    // Daily: original behaviour – only times matter
    return selectedTimes.length > 0;
  }, [scheduleType, selectedTimes, selectedWeekdays]);

  const canConfirm = useMemo(() => {
    if (!selectedMedication) return false;

    // Removing mode: require non-empty reason
    if (mode === 'edit' && removeMode) {
      return removeReason.trim().length > 0;
    }

    // Normal add/edit:
    //   - either valid basic selection (depends on scheduleType)
    //   - or at least one advanced entry
    return hasBasicSelection || hasAdvanced();
  }, [selectedMedication, mode, removeMode, removeReason, hasBasicSelection, advanced]);

  // days in month helper
  const daysInMonth = (y: number, m: number) => new Date(y, m, 0).getDate(); // m = 1..12
  const dayOptions = useMemo(() => {
    const y = typeof endYear === 'number' ? endYear : thisYear;
    const m = typeof endMonth === 'number' ? endMonth : 1;
    const cnt = daysInMonth(y, m);
    return Array.from({ length: cnt }, (_, i) => i + 1);
  }, [endYear, endMonth, thisYear]);

  const endDateString = useMemo(() => {
    if (endYear && endMonth && endDay) {
      const dd = String(endDay).padStart(2, '0');
      const mm = String(endMonth).padStart(2, '0');
      return `${dd}.${mm}.${endYear}`;
    }
    return '';
  }, [endYear, endMonth, endDay]);

  const generateInstruction = () => {
    // Prefer advanced grid if any entries
    if (hasAdvanced()) {
      const dayParts: string[] = [];
      for (let d = 0; d < 7; d++) {
        const entries = advanced[d];
        if (!entries || Object.keys(entries).length === 0) continue;
        const hours = Object.keys(entries).sort((a, b) => parseInt(a) - parseInt(b));
        const perDay = hours
          .map((h) => `${entries[h]} ${word(entries[h])} kl ${h.padStart(2, '0')}`)
          .join(' og ');
        dayParts.push(`${dayLabelsShort[d]}: ${perDay}`);
      }
      const base = dayParts.join('; ');
      return endDateString ? `${base}; til ${endDateString}` : base;
    }

    // --- NEW: weekly basic schedule ---
    if (scheduleType === 'weekly') {
      const times = [...selectedTimes].sort((a, b) => parseInt(a, 10) - parseInt(b, 10));
      if (times.length === 0 || selectedWeekdays.length === 0) {
        return endDateString ? `til ${endDateString}` : '';
      }

      // Build dose + time phrases, e.g.
      // "2 töflur kl 08", "1 tafla kl 14"
      const timeParts = times.map((h) => {
        const dose = doses[h] ?? 1;
        return `${dose} ${word(dose)} kl ${h.padStart(2, '0')}`;
      });

      // Join them nicely:
      // 1 → "2 töflur kl 08"
      // 2 → "2 töflur kl 08 og 1 tafla kl 14"
      // 3+ → "2 töflur kl 08, 1 tafla kl 14 og 1 tafla kl 20"
      let timesPart: string;
      if (timeParts.length === 1) {
        timesPart = timeParts[0];
      } else {
        timesPart = `${timeParts.slice(0, -1).join(', ')} og ${timeParts[timeParts.length - 1]}`;
      }

      // Weekdays part: "Þri og Fim", "Mán, Mið og Fös", etc.
      const dayNames = selectedWeekdays
        .slice()
        .sort((a, b) => a - b)
        .map((idx) => dayLabelsShort[idx]);

      const daysPart =
        dayNames.length === 1
          ? dayNames[0]
          : `${dayNames.slice(0, -1).join(', ')} og ${dayNames[dayNames.length - 1]}`;

      // Final weekly string
      const base = `${timesPart} vikulega á ${daysPart}`;

      return endDateString ? `${base} til ${endDateString}` : base;
    }

    // --- existing daily basic schedule ---
    const times = [...selectedTimes].sort((a, b) => parseInt(a) - parseInt(b));
    if (times.length === 0) return endDateString ? `til ${endDateString}` : '';
    if (times.length === 1) {
      const h = times[0];
      const n = doses[h] ?? 1;
      const base = `${n} ${word(n)} daglega kl ${h.padStart(2, '0')}`;
      return endDateString ? `${base} til ${endDateString}` : base;
    }
    const parts = times.map((h) => {
      const n = doses[h] ?? 1;
      return `${n} ${word(n)} kl ${h.padStart(2, '0')}`;
    });
    const base = `${parts.join(' og ')} daglega`;
    return endDateString ? `${base} til ${endDateString}` : base;
  };

  const handleConfirm = () => {
    if (!selectedMedication) return;

    // Removal flow (edit mode + remove toggle ON)
    if (mode === 'edit' && removeMode) {
      const reason = removeReason.trim();
      if (!reason) return; // safety – button is already disabled

      onConfirm({
        name: `${selectedMedication.name} ${selectedMedication.dose}`,
        times: [],
        indication,
        instruction: reason,
        removed: true,
        scheduleType,
      });
      return;
    }

    // Normal add / edit flow
    if (selectedTimes.length === 0 && !hasAdvanced()) return;

    // Build endDate object if present
    const endDate =
      endYear && endMonth && endDay
        ? { year: endYear as number, month: endMonth as number, day: endDay as number }
        : undefined;

    onConfirm({
      name: `${selectedMedication.name} ${selectedMedication.dose}`,
      // If we are using the advanced grid, we don't need basic times
      times: hasAdvanced() ? [] : selectedTimes,
      indication,
      instruction: generateInstruction(),
      scheduleType,
      doses: !hasAdvanced() ? { ...doses } : undefined,
      weekdays: scheduleType === 'weekly' && !hasAdvanced() ? [...selectedWeekdays] : undefined,
      advanced: hasAdvanced() ? advanced : undefined,
      endDate,
    });

    // reset only for "add" mode; for edit the parent will close the modal
    if (mode === 'add') {
      setQuery('');
      setSelectedMedication(null);
      setSelectedTimes([]);
      setDoses({});
      setAdvanced({});
      setShowAdvanced(false);
      setShowEndDate(false);
      setEndYear('');
      setEndMonth('');
      setEndDay('');
      setIndication('');
      setRemoveMode(false);
      setRemoveReason('');
      setSelectedWeekdays([]);
    }
  };

  return (
    <div className="add-med-form">
      <div className="medication-card two-column">
        {!selectedMedication ? (
          <>
            <div className="left-col">
              <div className="search-section">
                <div className="search-wrapper">
                  <input
                    type="text"
                    className="med-search-input"
                    placeholder="Leita að lyfi"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                  />
                  <div className="search-icon-med">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth="1.5"
                      stroke="currentColor"
                      className="search-svg-med"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z"
                      />
                    </svg>
                  </div>
                </div>
                {filteredOptions.length > 0 && (
                  <ul className="med-suggestions">
                    {filteredOptions.map((option, index) => (
                      <li
                        key={index}
                        onClick={() => {
                          setSelectedMedication(option);
                          // Always resolve to a concrete ScheduleType
                          const nextType: ScheduleType =
                            option.scheduleType ?? propScheduleType ?? 'daily';

                          setScheduleType(nextType);
                        }}
                      >
                        {option.name} {option.dose}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
            {/* bottom right actions */}
            <div className="form-actions no-med">
              <button className="cancel-btn" onClick={onCancel}>
                Hætta við
              </button>
              <button className="confirm-btn" disabled={!canConfirm} onClick={handleConfirm}>
                Staðfesta
              </button>
            </div>
          </>
        ) : (
          <>
            {/* LEFT COLUMN */}
            <div className="left-col">
              <div className="med-name-selected">
                {selectedMedication.name} {selectedMedication.dose}
              </div>

              <div className="time-selection">
                {Array.from({ length: 19 }).map((_, i) => {
                  const hour = (i + 6).toString();
                  const selected = selectedTimes.includes(hour);
                  return (
                    <div key={hour} className="time-cell">
                      <button
                        className={`time-btn ${selected ? 'selected' : ''}`}
                        onClick={() => toggleTime(hour)}
                      >
                        {hour}
                      </button>

                      {selected && (
                        <input
                          type="number"
                          min={0.5}
                          step={0.5}
                          inputMode="decimal"
                          className="dose-input"
                          value={doses[hour] ?? 1}
                          onChange={(e) => handleDoseChange(hour, e.target.value)}
                          aria-label={`Fjöldi taflna kl ${hour}`}
                        />
                      )}
                    </div>
                  );
                })}
              </div>

              {scheduleType === 'weekly' && (
                <div className="weekday-row">
                  <div className="weekday-label">Vikudagur meðferðar</div>
                  <div className="weekday-chips">
                    {dayLabelsShort.map((label, idx) => (
                      <button
                        key={label}
                        type="button"
                        className={
                          'weekday-chip' + (selectedWeekdays.includes(idx) ? ' active' : '')
                        }
                        onClick={() => toggleWeekday(idx)}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="extra-fields">
                <div className="field">
                  <label>Ábending meðferðar</label>
                  <input
                    type="text"
                    className="indication-input"
                    value={indication}
                    onChange={(e) => setIndication(e.target.value)}
                    placeholder="T.d. hjartabilun"
                  />
                </div>

                <div className="field">
                  <label>Skömmtunarfyrirmæli</label>
                  {(() => {
                    const instruction = generateInstruction().trim();
                    return instruction ? (
                      <div className="instruction-preview">{instruction}</div>
                    ) : null;
                  })()}
                </div>
              </div>
            </div>

            {/* RIGHT COLUMN */}
            <div className="right-col">
              {/* top row: always visible */}
              <div className="right-col-actions">
                <button
                  className={showAdvanced ? 'active' : ''}
                  onClick={() =>
                    setShowAdvanced((prev) => {
                      const next = !prev;
                      if (next) {
                        setShowEndDate(false);
                        setRemoveMode(false);
                      }
                      return next;
                    })
                  }
                >
                  Flóknari fyrirmæli
                </button>

                <button
                  className={showEndDate ? 'active' : ''}
                  onClick={() =>
                    setShowEndDate((prev) => {
                      const next = !prev;
                      if (next) {
                        setShowAdvanced(false);
                        setRemoveMode(false);
                      }
                      return next;
                    })
                  }
                >
                  Tilgreina lokadagsetningu
                </button>
              </div>

              {/* second row: remove button only when editing */}
              {mode === 'edit' && (
                <div className="right-col-actions">
                  <button
                    className={`remove-btn ${removeMode ? 'active' : ''}`}
                    onClick={() =>
                      setRemoveMode((prev) => {
                        const next = !prev;
                        if (next) {
                          setShowAdvanced(false);
                          setShowEndDate(false);
                        }
                        return next;
                      })
                    }
                  >
                    Taka lyf úr skömmtun
                  </button>
                </div>
              )}

              {/* remove reason panel, under buttons, same column width */}
              {mode === 'edit' && removeMode && (
                <div className="remove-reason-panel">
                  <label className="remove-reason-label">
                    Ástæða þess að lyf er tekið úr skömmtun
                  </label>
                  <textarea
                    className="remove-reason-input"
                    value={removeReason}
                    onChange={(e) => setRemoveReason(e.target.value)}
                    placeholder="Skrifaðu ástæðu..."
                  />
                </div>
              )}
            </div>

            {/* END DATE PANEL (right column) */}
            {showEndDate && (
              // <div className="enddate-panel panel-wide">
              <div className="enddate-panel">
                <div className="enddate-title">Tilgreina lokadagsetningu</div>
                <div className="enddate-row">
                  <span>Dagur</span>
                  <select
                    className="end-select"
                    value={endDay === '' ? '' : String(endDay)}
                    onChange={(e) => setEndDay(e.target.value ? parseInt(e.target.value, 10) : '')}
                  >
                    <option value="">—</option>
                    {dayOptions.map((d) => (
                      <option key={d} value={d}>
                        {d}
                      </option>
                    ))}
                  </select>

                  <span>Mánuður</span>
                  <select
                    className="end-select"
                    value={endMonth === '' ? '' : String(endMonth)}
                    onChange={(e) =>
                      setEndMonth(e.target.value ? parseInt(e.target.value, 10) : '')
                    }
                  >
                    <option value="">—</option>
                    {monthsIS.map((m, i) => (
                      <option key={m} value={i + 1}>
                        {m}
                      </option>
                    ))}
                  </select>

                  <span>Ár</span>
                  <select
                    className="end-select"
                    value={endYear === '' ? '' : String(endYear)}
                    onChange={(e) => setEndYear(e.target.value ? parseInt(e.target.value, 10) : '')}
                  >
                    <option value="">—</option>
                    {years.map((y) => (
                      <option key={y} value={y}>
                        {y}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            {/* ADVANCED GRID (spans both columns) */}
            {showAdvanced && (
              <div className="advanced-grid panel-wide">
                <div className="hours-header">
                  <span className="spacer" />
                  {Array.from({ length: 24 }).map((_, i) => (
                    <span key={i} className="hour-label">
                      {i + 1}
                    </span>
                  ))}
                </div>

                {Array.from({ length: 7 }).map((_, dIdx) => {
                  const row = advanced[dIdx] || {};
                  return (
                    <div key={dIdx} className="day-row">
                      <div className="day-label">{dayLabelsLong[dIdx]}</div>
                      <div className="day-cells">
                        {Array.from({ length: 24 }).map((__, h) => {
                          const hour = (h + 1).toString().padStart(2, '0');
                          const active = !!row[hour];
                          return (
                            <div
                              key={hour}
                              className={`adv-cell ${active ? 'active' : ''}`}
                              onClick={() => toggleAdvancedCell(dIdx, hour)}
                            >
                              {active && (
                                <input
                                  type="number"
                                  min={0}
                                  step={0.5}
                                  inputMode="decimal"
                                  className="adv-dose"
                                  value={row[hour]}
                                  onClick={(e) => e.stopPropagation()}
                                  onChange={(e) => setAdvancedDose(dIdx, hour, e.target.value)}
                                />
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            {/* BOTTOM ACTIONS */}
            <div className="form-actions with-med">
              <button className="cancel-btn" onClick={onCancel}>
                Hætta við
              </button>
              <button className="confirm-btn" disabled={!canConfirm} onClick={handleConfirm}>
                Staðfesta
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AddMedicationForm;
